/**
 * Streaming CSV parse: `parseFile(source, options)` → async iterable of rows.
 *
 * Memory is bounded by the largest single record plus one input chunk, so a 50 MB file parses in a few MB
 * of working set. See tokenizer.ts for the dialect and decode.ts for BOM / encoding handling.
 */

import { ChunkDecoder } from "./decode";
import { chunksOf } from "./source";
import { detectDelimiter, Tokenizer } from "./tokenizer";
import {
  type CsvDelimiter,
  type CsvMeta,
  CsvParseError,
  type CsvParseHandle,
  type CsvRow,
  type CsvSource,
  type ParseOptions,
} from "./types";

/** Decoded characters buffered before the delimiter is auto-detected. */
const DELIMITER_SNIFF_CHARS = 4096;
const DEFAULT_MAX_FIELD_LENGTH = 1_000_000;

function freshMeta(): CsvMeta {
  return {
    delimiter: null,
    encoding: null,
    hadBom: false,
    bytesRead: 0,
    rowsYielded: 0,
    blankLinesSkipped: 0,
    replacementChars: 0,
  };
}

function abortError(): Error {
  const error = new Error("The import was cancelled.");
  error.name = "AbortError";
  return error;
}

/**
 * Parse CSV from a `File` / `Blob`, a byte or string stream, an (async) iterable of chunks, a string or bytes.
 *
 * The returned handle is single-use. `handle.meta` fills in as the stream is read (delimiter and encoding
 * are known after the first rows arrive). Throws `CsvParseError` on an unterminated quote or a runaway field.
 *
 * @example
 * ```ts
 * for await (const row of parseFile(file)) console.log(row.index, row.cells);
 * ```
 */
export function parseFile(source: CsvSource, options: ParseOptions = {}): CsvParseHandle {
  const meta = freshMeta();
  let consumed = false;

  async function* run(): AsyncGenerator<CsvRow> {
    const { signal, onProgress } = options;
    const requestedDelimiter = options.delimiter ?? "auto";
    const decoder = new ChunkDecoder(options.encoding ?? "auto", meta);
    const tokenizerOptions = {
      skipEmptyLines: options.skipEmptyLines ?? true,
      maxFieldLength: options.maxFieldLength ?? DEFAULT_MAX_FIELD_LENGTH,
    };

    let tokenizer: Tokenizer | null = null;
    let pending = "";
    const out: CsvRow[] = [];

    const openTokenizer = (delimiter: CsvDelimiter): Tokenizer => {
      meta.delimiter = delimiter;
      return new Tokenizer({ delimiter, ...tokenizerOptions });
    };

    const feed = (text: string, last: boolean): void => {
      if (tokenizer === null) {
        if (requestedDelimiter !== "auto") {
          tokenizer = openTokenizer(requestedDelimiter);
        } else {
          pending += text;
          if (pending.length < DELIMITER_SNIFF_CHARS && !last) return;
          tokenizer = openTokenizer(detectDelimiter(pending, last));
          text = pending;
          pending = "";
        }
      }
      tokenizer.push(text, out);
      if (last) tokenizer.finish(out);
      meta.rowsYielded = tokenizer.rowsYielded;
      meta.blankLinesSkipped = tokenizer.blankLinesSkipped;
    };

    // A parse error must not swallow the records completed before it: yield those first, then rethrow.
    const feedSafely = (text: string, last: boolean): unknown => {
      try {
        feed(text, last);
        return undefined;
      } catch (error) {
        return error ?? new Error("CSV parse failed");
      }
    };

    const empty = new Uint8Array(0);
    for await (const chunk of chunksOf(source)) {
      if (signal?.aborted) throw abortError();
      let text: string;
      if (typeof chunk === "string") {
        meta.bytesRead += chunk.length;
        text = decoder.passthrough(chunk);
      } else {
        meta.bytesRead += chunk.byteLength;
        text = decoder.decode(chunk, false);
      }
      onProgress?.(meta.bytesRead);
      const failure = text.length > 0 ? feedSafely(text, false) : undefined;
      for (const row of out) yield row;
      out.length = 0;
      if (failure !== undefined) throw failure;
    }
    if (signal?.aborted) throw abortError();

    // Flush the decoder (a trailing partial multi-byte sequence) and the tokenizer (the last record).
    const failure = feedSafely(decoder.decode(empty, true), true);
    for (const row of out) yield row;
    out.length = 0;
    if (failure !== undefined) throw failure;
  }

  return {
    meta,
    [Symbol.asyncIterator](): AsyncIterator<CsvRow> {
      if (consumed) {
        throw new CsvParseError(
          "already_consumed",
          "This CSV stream was already read. Call parseFile again to read it a second time."
        );
      }
      consumed = true;
      return run();
    },
  };
}

/**
 * Synchronous parse of an in-memory string (a pasted textarea, a small fixture, a response body already in
 * hand). Same dialect, BOM handling and delimiter detection as `parseFile`, same `CsvRow` shape, same
 * `CsvParseError` on an unterminated quote. Use `parseFile` for anything that can be large.
 *
 * @example
 * ```ts
 * const grid = parseCsvText(textarea.value).map((row) => row.cells);
 * ```
 */
export function parseCsvText(
  text: string,
  options: Pick<ParseOptions, "delimiter" | "skipEmptyLines" | "maxFieldLength"> = {}
): CsvRow[] {
  const body = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const requested = options.delimiter ?? "auto";
  const delimiter =
    requested === "auto"
      ? detectDelimiter(body.slice(0, DELIMITER_SNIFF_CHARS), body.length <= DELIMITER_SNIFF_CHARS)
      : requested;
  const tokenizer = new Tokenizer({
    delimiter,
    skipEmptyLines: options.skipEmptyLines ?? true,
    maxFieldLength: options.maxFieldLength ?? DEFAULT_MAX_FIELD_LENGTH,
  });
  const rows: CsvRow[] = [];
  tokenizer.push(body, rows);
  tokenizer.finish(rows);
  return rows;
}

export interface CsvPreview {
  /** The first `maxRows` records (the header, if any, is rows[0]). */
  rows: CsvRow[];
  /** More records exist beyond `rows`. */
  truncated: boolean;
  meta: CsvMeta;
}

/**
 * Read just the first records of a source — enough for a header row and a sample — then stop reading.
 * The source is not consumed past the point needed, so a huge file previews instantly.
 */
export async function previewFile(
  source: CsvSource,
  options: ParseOptions & { maxRows?: number } = {}
): Promise<CsvPreview> {
  const { maxRows = 20, ...parseOptions } = options;
  const handle = parseFile(source, parseOptions);
  const rows: CsvRow[] = [];
  let truncated = false;
  for await (const row of handle) {
    if (rows.length >= maxRows) {
      truncated = true;
      break;
    }
    rows.push(row);
  }
  return { rows, truncated, meta: { ...handle.meta } };
}
